import { create } from 'zustand';
import { SupplierOrderListResponse, SupplierOrderDetailResponse, SupplierLovCustomer, OrderStatus } from '@api/generated/search-service';
import { supplierOrderControllerApi, supplierLOVApi, workflowApi } from '@api/api-config';
import { useOrgPortal } from '@hooks/useOrgPortal.ts';

interface SupplierOrderFilters {
  page?: number;
  size?: number;
  query?: string;
  organizationId?: number;
  orderStatus?: OrderStatus;
  deliveryDateFrom?: string;
  deliveryDateTo?: string;
}

export interface WorkflowAction {
  actionId: string;
  label: string;
}

interface SupplierOrderState {
  // List state
  orders: SupplierOrderListResponse[];
  total: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
  hasNext: boolean;
  hasPrev: boolean;
  isLoading: boolean;
  error: string | null;
  filters: SupplierOrderFilters;

  // LOV state
  customers: SupplierLovCustomer[];
  isLoadingCustomers: boolean;

  // Detail state
  orderDetail: SupplierOrderDetailResponse | null;
  isLoadingDetail: boolean;
  errorDetail: string | null;

  // Workflow state
  workflowActions: WorkflowAction[];
  isLoadingActions: boolean;

  // Actions
  fetchOrders: (filters?: Partial<SupplierOrderFilters>) => Promise<void>;
  fetchOrderDetail: (orderId: number) => Promise<void>;
  fetchCustomers: () => Promise<void>;
  fetchWorkflowActions: (workflowInstanceId: number) => Promise<void>;
  completeWorkflowAction: (workflowInstanceId: number, actionId: string, comment?: string, variables?: Record<string, any>) => Promise<void>;
  setFilters: (filters: Partial<SupplierOrderFilters>) => void;
  clearFilters: () => void;
  clearError: () => void;
  clearDetail: () => void;
}

const defaultFilters: SupplierOrderFilters = {
  page: 1,
  size: 20,
};

export const useSupplierOrderStore = create<SupplierOrderState>(set => ({
  // List state
  orders: [],
  total: 0,
  totalPages: 0,
  currentPage: 1,
  pageSize: 20,
  hasNext: false,
  hasPrev: false,
  isLoading: false,
  error: null,
  filters: defaultFilters,

  // LOV state
  customers: [],
  isLoadingCustomers: false,

  // Detail state
  orderDetail: null,
  isLoadingDetail: false,
  errorDetail: null,

  // Workflow state
  workflowActions: [],
  isLoadingActions: false,

  fetchCustomers: async () => {
    set({ isLoadingCustomers: true });

    try {
      const { orgId: supplierId, portalId } = useOrgPortal.getState();

      if (!supplierId || !portalId) {
        set({ isLoadingCustomers: false });
        return;
      }

      const response = await supplierLOVApi.getSupplierLovCustomers({
        supplierId,
        portalId,
      });

      set({
        customers: response.data || [],
        isLoadingCustomers: false,
      });
    } catch (err) {
      console.error('Error fetching customers:', err);
      set({ isLoadingCustomers: false });
    }
  },

  fetchOrders: async (newFilters?: Partial<SupplierOrderFilters>) => {
    set({ isLoading: true, error: null });

    try {
      const { orgId: supplierId, portalId } = useOrgPortal.getState();

      if (!supplierId || !portalId) {
        set({ error: 'Dodavatel nebo portál nebyl nalezen', isLoading: false });
        return;
      }

      // Update filters if provided
      if (newFilters) {
        set(state => ({
          filters: { ...state.filters, ...newFilters },
        }));
      }

      // Get current filters
      const currentState = useSupplierOrderStore.getState();
      const filters = { ...currentState.filters, ...newFilters };

      const response = await supplierOrderControllerApi.getSupplierOrders({
        supplierId,
        portalId,
        page: filters.page,
        size: filters.size,
        query: filters.query,
        organizationId: filters.organizationId,
        orderStatus: filters.orderStatus,
        deliveryDateFrom: filters.deliveryDateFrom,
        deliveryDateTo: filters.deliveryDateTo,
      });

      set({
        orders: response.data?.items || [],
        total: response.data?.total || 0,
        totalPages: response.data?.totalPages || 0,
        currentPage: response.data?.page || 1,
        pageSize: response.data?.size || 20,
        hasNext: response.data?.hasNext || false,
        hasPrev: response.data?.hasPrev || false,
        isLoading: false,
        error: null,
      });
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Nepodařilo se načíst objednávky';
      set({
        error: errorMessage,
        isLoading: false,
      });
    }
  },

  fetchOrderDetail: async (orderId: number) => {
    set({ isLoadingDetail: true, errorDetail: null });

    try {
      const { orgId: supplierId, portalId } = useOrgPortal.getState();

      if (!supplierId || !portalId) {
        set({ errorDetail: 'Dodavatel nebo portál nebyl nalezen', isLoadingDetail: false });
        return;
      }

      const response = await supplierOrderControllerApi.getSupplierOrderDetail({
        supplierId,
        portalId,
        orderId,
      });

      set({
        orderDetail: response.data || null,
        isLoadingDetail: false,
        errorDetail: null,
      });

      // Pokud má workflow, načíst dostupné akce
      const workflowInstanceId = response.data?.workflowInstanceId;
      if (workflowInstanceId) {
        await useSupplierOrderStore.getState().fetchWorkflowActions(workflowInstanceId);
      } else {
        set({ workflowActions: [] });
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Nepodařilo se načíst detail objednávky';
      set({
        errorDetail: errorMessage,
        isLoadingDetail: false,
      });
    }
  },

  fetchWorkflowActions: async (workflowInstanceId: number) => {
    set({ isLoadingActions: true });

    try {
      const { orgId: organizationId, portalId } = useOrgPortal.getState();

      if (!organizationId || !portalId) {
        throw new Error('Organizace nebo portál nebyl nalezen');
      }

      const response = await workflowApi.getWorkflowTaskActions({
        instanceId: workflowInstanceId,
        organizationId,
        portalId,
      });

      set({
        workflowActions: response.data || [],
        isLoadingActions: false,
      });
    } catch (err) {
      console.error('Error fetching workflow actions:', err);
      set({ workflowActions: [], isLoadingActions: false });
    }
  },

  completeWorkflowAction: async (workflowInstanceId: number, actionId: string, comment?: string, variables?: Record<string, any>) => {
    const { orgId: organizationId, portalId } = useOrgPortal.getState();

    if (!organizationId || !portalId) {
      throw new Error('Organizace nebo portál nebyl nalezen');
    }

    await workflowApi.completeWorkflowTask({
      instanceId: workflowInstanceId,
      organizationId,
      portalId,
      workflowCompleteRequest: {
        actionId,
        comment,
        variables: variables || {},
      },
    });

    // Refresh detail + workflow actions
    const orderDetail = useSupplierOrderStore.getState().orderDetail;
    if (orderDetail) {
      await useSupplierOrderStore.getState().fetchOrderDetail(orderDetail.id);
    }
  },

  setFilters: (newFilters: Partial<SupplierOrderFilters>) => {
    set(state => ({
      filters: { ...state.filters, ...newFilters, page: 1 }, // Reset to first page on filter change
    }));
  },

  clearFilters: () => {
    set({ filters: defaultFilters });
  },

  clearError: () => {
    set({ error: null });
  },

  clearDetail: () => {
    set({ orderDetail: null, errorDetail: null, workflowActions: [] });
  },
}));
